// Who left the repertoire, and where (decided 2026-11-01). The server answers one question per position — is this
// position in an active repertoire? — and the screen used to blame whoever made the last move: you played out of
// the book, your opponent answered, and the card said "your opponent played outside the repertoire". The one who
// left is whoever made the move after the last position that was in it, and that stays the answer for every move
// after, until the game comes back into the repertoire by a transposition (the server then says "in" again).

export type Mover = "user" | "rival";

/** What the repertoire said of the position after `ply` half moves of the game on the board. */
export interface RepertoireCheck {
    inRepertoire: boolean;
    /** The repertoire's moves from this position, in UCI (a line each, its first move first). */
    nextMoves: string[];
    /** The position, for writing those moves down. */
    fen: string;
}

export interface Deviation {
    by: Mover;
    /** The half move that left it: 1 is the first move of the game on the board. */
    ply: number;
    /** That move, as played. */
    san: string;
    /** What the repertoire had there, in UCI, and the position they are moves of. */
    expected: string[];
    expectedFen: string;
}

/**
 * The deviation that explains the position after `ply` half moves, or null when that position is in the repertoire
 * or there is no position before it known to be in it (a line that never was).
 *
 * @param checks  what the repertoire said, indexed by half moves played; holes are positions never asked about
 * @param sans    the moves of the game, in order
 * @param moverOf who made the half move with that number (1-based)
 */
export function deviationAt(checks: (RepertoireCheck | undefined)[], sans: string[], ply: number,
                            moverOf: (ply: number) => Mover): Deviation | null {
    if (checks[ply]?.inRepertoire) return null;
    for (let known = ply - 1; known >= 0; known--) {
        const check = checks[known];
        if (!check?.inRepertoire) continue;
        const left = known + 1;
        return {
            by: moverOf(left),
            ply: left,
            san: sans[left - 1] ?? "",
            expected: check.nextMoves,
            expectedFen: check.fen,
        };
    }
    return null;
}

/** Who made half move `ply`, from who is to move after `current` half moves and which side the user plays. */
export function moverFrom(turnNow: "w" | "b", current: number, userSide: "white" | "black"): (ply: number) => Mover {
    const lastMover = turnNow === "w" ? "b" : "w";
    return (ply) => {
        const side = (current - ply) % 2 === 0 ? lastMover : turnNow;
        return side === userSide[0] ? "user" : "rival";
    };
}
